import React, { useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle, Clock, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { adminGetLatestAudit, adminListAuditRuns, adminRunAudit } from '../services/api';

const fmtDuration = (s) => {
  if (s == null) return '';
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.round(s / 60)}m ${Math.round(s % 60)}s`;
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
};

const AuditPanel = ({ token, onChanged }) => {
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showBroken, setShowBroken] = useState(false);
  const [error, setError] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const [run, runs] = await Promise.all([
        adminGetLatestAudit(token),
        adminListAuditRuns(token, 10),
      ]);
      setLatest(run);
      setHistory(runs);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const handleRun = async () => {
    setRunning(true); setError(null);
    try {
      const summary = await adminRunAudit(token, true);
      setLatest(summary);
      await refresh();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Audit failed');
    } finally {
      setRunning(false);
    }
  };

  const brokenList = latest?.broken_details || [];
  const brokenNonProtected = brokenList.filter((b) => !b.protected);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900 text-[15px]">URL audit</h3>
              <span className="text-[10.5px] font-medium px-1.5 py-0.5 rounded-full border border-slate-200 text-slate-500 uppercase tracking-wide">
                weekly
              </span>
            </div>
            {loading && !latest ? (
              <p className="text-[12.5px] text-slate-500 mt-1">Loading…</p>
            ) : !latest ? (
              <p className="text-[12.5px] text-slate-500 mt-1">No audits yet.</p>
            ) : (
              <>
                <p className="text-[12.5px] text-slate-600 mt-1">
                  Last run <span className="font-medium text-slate-900">{fmtWhen(latest.started_at)}</span>
                  <span className="text-slate-400"> · {latest.triggered_by}</span>
                  <span className="text-slate-400"> · {fmtDuration(latest.duration_s)}</span>
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11.5px]">
                  <Stat label="audited" value={latest.audited} tone="slate" />
                  <Stat label="ok" value={latest.ok} tone="emerald" />
                  <Stat label="broken" value={latest.broken} tone={latest.broken > 0 ? 'amber' : 'slate'} />
                  <Stat label="deleted" value={latest.deleted_count} tone={latest.deleted_count > 0 ? 'rose' : 'slate'} />
                  <Stat label="kept (protected)" value={latest.protected_kept} tone="slate" />
                  <Stat label="now in db" value={latest.total_after} tone="slate" />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button
            onClick={handleRun}
            disabled={running}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] font-medium bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {running ? 'Running…' : 'Run now'}
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] font-medium bg-white border border-slate-200 hover:border-slate-300 text-slate-600"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 text-[12.5px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-2">
          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {brokenList.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <button
            onClick={() => setShowBroken(!showBroken)}
            className="text-[12px] font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
          >
            {showBroken ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            Show broken URLs ({brokenList.length}, {brokenNonProtected.length} non-protected)
          </button>
          {showBroken && (
            <ul className="mt-2 space-y-1 text-[12px]">
              {brokenList.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-600">
                  <AlertTriangle size={11} className={`mt-1 flex-shrink-0 ${b.protected ? 'text-slate-400' : 'text-amber-500'}`} />
                  <span className="font-medium text-slate-900 min-w-[150px]">{b.name}</span>
                  <span className="text-slate-400 truncate flex-1">{b.website}</span>
                  <span className="text-slate-500 text-[11px]">{b.reason}</span>
                  {b.protected && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 uppercase">
                      protected
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {history.length > 1 && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-[12px] font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
          >
            {showHistory ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            History ({history.length} runs)
          </button>
          {showHistory && (
            <table className="mt-2 w-full text-[11.5px]">
              <thead className="text-slate-500">
                <tr>
                  <th className="text-left py-1 font-medium">When</th>
                  <th className="text-left py-1 font-medium">Trigger</th>
                  <th className="text-right py-1 font-medium">Audited</th>
                  <th className="text-right py-1 font-medium">OK</th>
                  <th className="text-right py-1 font-medium">Broken</th>
                  <th className="text-right py-1 font-medium">Deleted</th>
                  <th className="text-right py-1 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {history.map((r) => (
                  <tr key={r.id}>
                    <td className="py-1.5"><Clock size={10} className="inline mr-1 text-slate-400" />{fmtWhen(r.started_at)}</td>
                    <td className="py-1.5 text-slate-500">{r.triggered_by}</td>
                    <td className="py-1.5 text-right">{r.audited}</td>
                    <td className="py-1.5 text-right text-emerald-700">{r.ok}</td>
                    <td className="py-1.5 text-right text-amber-700">{r.broken}</td>
                    <td className="py-1.5 text-right text-rose-700">{r.deleted_count}</td>
                    <td className="py-1.5 text-right text-slate-500">{fmtDuration(r.duration_s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-50 text-emerald-800',
    amber: 'bg-amber-50 text-amber-800',
    rose: 'bg-rose-50 text-rose-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${tones[tone] || tones.slate}`}>
      <span className="font-semibold">{value}</span>
      <span className="text-[10.5px]">{label}</span>
    </span>
  );
};

export default AuditPanel;
